import React, { useState, lazy, Suspense, useRef, useEffect } from 'react';
import LoadingSkeleton from './components/LoadingSkeleton';
import { logWarning } from './utils/logger';
import { getLocalCalendarDay } from './utils/dateUtils';
// Lazy load knowledge base instead of eager Trivia
// import TriviaView from './components/TriviaView';
import { useAuth } from './context/AuthContext';
import { useAudioEngine } from './hooks/useAudioEngine';
import { useMorseGame } from './hooks/useMorseGame';
import { useSocial } from './hooks/useSocial';
import { useSignalAnalyticEngine } from './hooks/useSignalAnalyticEngine';

// MenuView is the default view - keep it eager loaded for fast initial render
import MenuView from './components/MenuView';

// Lazy load ALL other views to reduce initial bundle size
const GameView = lazy(() => import('./components/GameView'));
const GameOverView = lazy(() => import('./components/GameOverView'));
const AuthView = lazy(() => import('./components/AuthView'));
const DictionaryView = lazy(() => import('./components/DictionaryView'));
const SettingsView = lazy(() => import('./components/SettingsView'));
const StatsView = lazy(() => import('./components/StatsView'));
const LeaderboardView = lazy(() => import('./components/LeaderboardView'));
const AdventureView = lazy(() => import('./components/AdventureView'));
const UserProfileView = lazy(() => import('./components/UserProfileView'));
const SocialView = lazy(() => import('./components/SocialView'));
const KnowledgeBaseView = lazy(() => import('./components/KnowledgeBaseView'));
const SkillAssessmentModal = lazy(() => import('./components/SkillAssessmentModal'));
const WaterfallView = lazy(() => import('./components/WaterfallView'));
const GameCenterView = lazy(() => import('./components/GameCenterView'));
const LegalView = lazy(() => import('./components/LegalView')); // Add LegalView
const LiteratureLibraryView = lazy(() => import('./components/LiteratureLibraryView'));
const LandingView = lazy(() => import('./components/LandingView'));
const MidnightExpressView = lazy(() => import('./components/experimental/MidnightExpress').then(module => ({ default: module.MidnightExpressView })));
const LabView = lazy(() => import('./components/LabView'));
const ProQSOView = lazy(() => import('./components/experimental/ProQSO/ProQSOView'));
const SignalLampView = lazy(() => import('./components/SignalLamp/SignalLampView'));
const TelegraphHangmanView = lazy(() => import('./components/TelegraphHangman/TelegraphHangmanView'));
const SignalWordView = lazy(() => import('./components/TheSignalWord'));
const ConstellationHunterView = lazy(() => import('./components/experimental/ConstellationHunter'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
import AppFooter from './components/common/AppFooter';

import { useWakeLock } from './hooks/useWakeLock';
import { useDataSync } from './hooks/useDataSync';
import { useDailyChallenge } from './hooks/useDailyChallenge';
import { useTrivia } from './hooks/useTrivia';
import { useModeTracking } from './hooks/useModeTracking';
import { SEO } from './components/SEO';
import { useStreakFreeze } from './hooks/useStreakFreeze';

const MorseApp = () => {
    const [userSettings, setUserSettings] = useState(() => {
        const saved = localStorage.getItem('morse_settings_v1');
        const parsed = saved ? JSON.parse(saved) : {
            callSign: '',
            country: 'US',
            regionCode: '',
            defaultWpm: 20,
            noiseLevel: 0,
            qsbLevel: 0,
            maxWpm: 20,
            yyzKeyed: false,
            cwoKeyed: false,
            micUsed: false,
            hapticsEnabled: true,
            theme: 'default' // Default theme
        };
        // Ensure maxWpm exists (migration)
        if (!parsed.maxWpm) parsed.maxWpm = parsed.defaultWpm || 20;
        // Ensure yyzKeyed exists (migration)
        if (parsed.yyzKeyed === undefined) parsed.yyzKeyed = false;
        // Ensure cwoKeyed exists (migration)
        if (parsed.cwoKeyed === undefined) parsed.cwoKeyed = false;
        // Ensure micUsed exists (migration)
        if (parsed.micUsed === undefined) parsed.micUsed = false;
        // Ensure streak exists (migration)
        if (parsed.streak === undefined) parsed.streak = 0;
        if (parsed.lastPlayedDate === undefined) parsed.lastPlayedDate = null;
        // Ensure streak freezes exist (migration)
        if (parsed.streakFreezes === undefined) parsed.streakFreezes = 1;
        if (parsed.streakFreezeLastEarned === undefined) parsed.streakFreezeLastEarned = new Date().toISOString();
        if (parsed.streakFreezeUsedDate === undefined) parsed.streakFreezeUsedDate = null;
        // Ensure unlockedTrivia exists (migration)
        if (parsed.unlockedTrivia === undefined) parsed.unlockedTrivia = [];
        // Ensure hapticsEnabled exists (migration)
        if (parsed.hapticsEnabled === undefined) parsed.hapticsEnabled = true;
        // Ensure achievements exists (migration)
        if (parsed.achievements === undefined) parsed.achievements = [];
        // Ensure lifetimeScore exists (migration)
        if (parsed.lifetimeScore === undefined) parsed.lifetimeScore = 0;
        // Fix for "3 billion point" bug (string concatenation) - cap at 100k if unreasonably high
        if (parsed.lifetimeScore > 2000000000) parsed.lifetimeScore = 100000;
        parsed.lifetimeScore = Number(parsed.lifetimeScore);
        // Ensure keyMap exists (migration)
        if (parsed.keyMap === undefined) parsed.keyMap = { dot: 'z', dash: 'x' };
        // Ensure gamepadMap exists (migration)
        if (parsed.gamepadMap === undefined) parsed.gamepadMap = { dot: 0, dash: 1 };
        // Ensure paddleReversed exists (migration)
        if (parsed.paddleReversed === undefined) parsed.paddleReversed = false;
        // Ensure qsbLevel exists (migration)
        if (parsed.qsbLevel === undefined) parsed.qsbLevel = 0;
        // Ensure theme exists (migration)
        if (parsed.theme === undefined) parsed.theme = 'default';
        // Ensure modesHistory exists (migration)
        if (parsed.modesHistory === undefined) parsed.modesHistory = [];
        // Ensure errorBuzzerVolume exists (migration)
        if (parsed.errorBuzzerVolume === undefined) parsed.errorBuzzerVolume = 1.0;
        return parsed;
    });

    const { user, loading } = useAuth();
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);

    // Prompt/Landing state
    const [landingDismissed, setLandingDismissed] = useState(() => {
        return sessionStorage.getItem('landing_dismissed') === 'true';
    });

    // Handle logout/reset of landing if someone logs out and wants to see it again? 
    // Usually, we just want to ensure we don't block them.

    // If user logs in, ensure prompt is closed and landing is effectively "skipped"
    React.useEffect(() => {
        if (user) {
            setShowLoginPrompt(false);
            setLandingDismissed(true);
        }
    }, [user]);

    const handleStartGuest = () => {
        setLandingDismissed(true);
        sessionStorage.setItem('landing_dismissed', 'true');
    };

    // Enforce Settings Ownership and Handle Logout Resets
    React.useEffect(() => {
        if (loading) return;

        // Define the "Clean Slate" defaults for resets
        const getDefaults = (newUserId = undefined) => ({
            callSign: '',
            username: '',
            country: 'US',
            regionCode: '',
            defaultWpm: 20,
            noiseLevel: 0,
            qsbLevel: 0,
            maxWpm: 20,
            yyzKeyed: false,
            cwoKeyed: false,
            micUsed: false,
            hapticsEnabled: true,
            theme: 'default',
            streak: 0,
            lastPlayedDate: null,
            unlockedTrivia: [],
            achievements: [],
            wpmHistory: [],
            modesHistory: [],
            contestCompleted: 0,
            pileupCompleted: 0,
            headCopyWords: 0,
            keyMap: { dot: 'z', dash: 'x' },
            gamepadMap: { dot: 0, dash: 1 },
            paddleReversed: false,
            skill_assessment_completed: false, // Explicitly false for new users
            skill_level: undefined,
            skill_level_updated_at: undefined,
            userId: newUserId, // Claim for this user or leave undefined for guest
            errorBuzzerVolume: 1.0
        });

        const resetProgress = {
            level: 1,
            maxLevel: 1,
            history: [],
            totalSessions: 0,
            accurateSessions: 0,
            totalCharsCopied: 0,
            callsignCount: 0,
            paddleCharsSent: 0,
            totalQSOs: 0,
            qsbSessions: 0,
            echoCompleted: 0
        };

        // CASE 1: LOGOUT DETECTED / STALE USER DATA
        // Settings belong to a user, but we are currently anonymous.
        if (!user && userSettings.userId) {
            console.warn('[Security] Logout detected or stale user data. Clearing sensitive fields.');

            const defaults = getDefaults(undefined);

            // Apply to state
            setUserSettings(defaults);
            game.setProgress(resetProgress);

            // Sync with Game state helpers
            game.setWpm(defaults.defaultWpm);
            game.setNoiseLevel(defaults.noiseLevel);
            game.setQsbLevel(defaults.qsbLevel);
            game.setCallSignRegion(defaults.country);

            // Persist
            localStorage.setItem('morse_settings_v1', JSON.stringify(defaults));
            localStorage.setItem('morse_progress_v2', JSON.stringify(resetProgress));
            return;
        }

        // CASE 2: IDENTITY MISMATCH (User changed without clearing local storage)
        if (user && userSettings.userId && userSettings.userId !== user.id) {
            console.warn('[Security] Identity mismatch in local settings. resetting to claim.');

            const defaults = getDefaults(user.id);

            setUserSettings(defaults);
            game.setProgress(resetProgress);

            localStorage.setItem('morse_settings_v1', JSON.stringify(defaults));
            localStorage.setItem('morse_progress_v2', JSON.stringify(resetProgress));
            return;
        }

        // CASE 3: ANONYMOUS CLAIM (Logged in, but settings are anonymous)
        if (user && !userSettings.userId) {
            console.log('[Auth] Claiming anonymous settings for user:', user.id);
            setUserSettings(prev => {
                const updated = { ...prev, userId: user.id };
                localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                return updated;
            });
        }
    }, [loading, user, userSettings.userId]);


    // Apply Theme
    React.useEffect(() => {
        const theme = userSettings.theme || 'default';
        if (theme === 'default') {
            document.documentElement.removeAttribute('data-theme');
        } else {
            document.documentElement.setAttribute('data-theme', theme);
        }
    }, [userSettings.theme]);

    const audio = useAudioEngine();

    // Sync error buzzer volume with user settings
    useEffect(() => {
        audio.setErrorVolume(userSettings.errorBuzzerVolume ?? 1.0);
    }, [userSettings.errorBuzzerVolume, audio]);

    const analyticEngine = useSignalAnalyticEngine({ wpm: userSettings.defaultWpm });

    // Daily Challenge Hook
    const { dailyChallenge, dailyDefinition, weeklyChallenge, weeklyDefinition, updateChallengeProgress } = useDailyChallenge(userSettings, setUserSettings);

    // Trivia Hook
    const { unlockTrivia } = useTrivia(userSettings, setUserSettings);

    // Streak Freeze Hook
    const streakShield = useStreakFreeze(userSettings, setUserSettings);

    // Ref for cloud sync (defined later, used in handleGameUpdate)
    const saveToCloudRef = useRef(null);

    const handleGameUpdate = (update) => {
        updateChallengeProgress(update);

        // Any meaningful game activity should count towards the daily streak
        const streakTriggeringTypes = [
            'score',
            'accuracy',
            'signal_lamp_success',
            'constellation_complete',
            'adventure_complete',
            'qso_complete',
            'level_complete',
            'pileup_success'
        ];

        if (streakTriggeringTypes.includes(update.type)) {
            // Track freeze action needed (computed inside setter with correct prev state)
            let pendingFreezeAction = null;

            setUserSettings(prev => {
                const today = getLocalCalendarDay();
                let newStreak = prev.streak || 0;
                let newLastPlayedDate = prev.lastPlayedDate;

                // Increment streak if first play of the day
                if (prev.lastPlayedDate !== today) {
                    newLastPlayedDate = today;

                    // IMPORTANT: Use prev.lastPlayedDate (correct value) not userSettings.lastPlayedDate (stale)
                    const protection = streakShield.shouldProtectStreak(prev.lastPlayedDate);

                    if (protection.shouldProtect) {
                        // Streak protected by freeze! Mark for consumption after state update
                        pendingFreezeAction = protection.requiredFreezes;
                        newStreak = (prev.streak || 0) + 1;
                    } else if (!protection.isBroken) {
                        // No protection needed (played yesterday)
                        newStreak = (prev.streak || 0) + 1;
                    } else {
                        // Streak truly broken
                        newStreak = 1;
                    }
                }

                // Calculate points to add
                let addedScore = update.amount || 0;
                if (!addedScore) {
                    if (update.type === 'qso_complete') addedScore = 500;
                    else if (update.type === 'adventure_complete') addedScore = 1000;
                    else if (update.type === 'signal_lamp_success') addedScore = 50;
                    else if (update.type === 'pileup_success') addedScore = 200;
                    else if (update.type === 'level_complete') addedScore = 250;
                }

                // Monitoring: Context-Aware Thresholds
                const isAdventure = update.mode === 'adventure' || update.type === 'adventure_complete';
                const singleUpdateThreshold = isAdventure ? 15000 : 2500;

                if (addedScore > singleUpdateThreshold) {
                    logWarning('Excessive Single Point Award', {
                        type: update.type,
                        amount: addedScore,
                        mode: isAdventure ? 'adventure' : 'standard',
                        user: userSettings.username
                    });
                }

                // Monitoring: Hourly Velocity Check (> 20k/hr)
                // We use a static array on the component instance (ref) if available, or just rely on session state if we refactor.
                // Since this is inside a state setter, we can't easily access refs without closing over them.
                // However, we can check the *previous* session state if we stored history there, but that bloats state.
                // A better approach for the setter callback is to rely on an external ref closed over by useEffect, but here we are in a callback.
                // Let's assume we have access to `scoreHistory` defined in the component scope.

                if (window.morseScoreHistory) {
                    const now = Date.now();
                    // Prune old entries (> 1 hour)
                    window.morseScoreHistory = window.morseScoreHistory.filter(e => now - e.time < 3600000);
                    // Add new
                    window.morseScoreHistory.push({ time: now, amount: addedScore });

                    const hourlyTotal = window.morseScoreHistory.reduce((sum, e) => sum + e.amount, 0);

                    if (hourlyTotal > 20000) {
                        // Debounce warning: only warn once per 5k increment to avoid spam
                        const lastWarned = window.morseLastVelocityWarn || 0;
                        if (hourlyTotal - lastWarned >= 5000) {
                            logWarning('High Hourly Point Velocity', {
                                total: hourlyTotal,
                                rate_limit: 20000,
                                user: userSettings.username
                            });
                            window.morseLastVelocityWarn = hourlyTotal;
                        }
                    }
                } else {
                    // Initialize if missing (e.g. first run)
                    window.morseScoreHistory = [{ time: Date.now(), amount: addedScore }];
                }

                // Monitoring: Major Event Frequency Check
                const majorEvents = ['qso_complete', 'adventure_complete', 'level_complete'];
                if (majorEvents.includes(update.type)) {
                    const now = Date.now();
                    const lastMajor = window.morseLastMajorEventTime || 0;
                    const timeSince = now - lastMajor;

                    // If a major event happens < 15 seconds after the last one, flag it
                    if (timeSince < 15000) {
                        logWarning('Suspicious Major Event Frequency', {
                            type: update.type,
                            timeSinceMs: timeSince,
                            user: userSettings.username
                        });
                    }
                    window.morseLastMajorEventTime = now;
                }

                const nextLifetimeScore = Number(prev.lifetimeScore || 0) + Number(addedScore);

                // Integrity Check: Prevent Corruption (NaN/Infinity)
                if (!Number.isFinite(nextLifetimeScore)) {
                    logWarning('Score Corruption Prevented (NaN/Infinity)', {
                        current: prev.lifetimeScore,
                        added: addedScore,
                        type: update.type
                    });
                    // Abort update to protect data
                    return prev;
                }

                const updated = {
                    ...prev,
                    lifetimeScore: nextLifetimeScore,
                    streak: newStreak,
                    lastPlayedDate: newLastPlayedDate
                };
                localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
                return updated;
            });

            // Consume freezes if needed (computed inside setter with correct prev state)
            if (pendingFreezeAction) {
                streakShield.useFreeze(pendingFreezeAction);
            }

            // Trigger immediate cloud sync for streak updates (important for data integrity)
            if (saveToCloudRef.current) {
                // Small delay to ensure state has propagated
                setTimeout(() => saveToCloudRef.current?.(), 100);
            }
        }
    };

    const game = useMorseGame(audio, userSettings, unlockTrivia, handleGameUpdate, setUserSettings, analyticEngine.recordMove, analyticEngine.recordElement);
    const social = useSocial();

    // Browser History Integration (Deep Linking & Back Button)
    React.useEffect(() => {
        // 1. Initial Load: Sync App State to URL
        const rawHash = window.location.hash.slice(1);
        let initialView = rawHash.split('#')[0];

        // Legacy Redirects
        if (initialView === 'privacy') initialView = 'legal';

        // Ignore Supabase Auth fragments (access_token, error_description, etc.)
        // This prevents the router from trying to render weird views like "access_token=..."
        const isAuthHash = rawHash.includes('access_token=') || rawHash.includes('error_description=') || rawHash.includes('type=signup');
        if (isAuthHash) {
            initialView = 'menu';
        }

        if (initialView && initialView !== 'menu') {
            game.setView(initialView);
            // Replace the initial state so we have a valid history object to go back to
            window.history.replaceState({ view: initialView }, '', `#${initialView}`);
        } else {
            // Ensure clean start
            // Only replace state if it wasn't an auth hash (let Supabase handle the hash)
            if (!isAuthHash) {
                window.history.replaceState({ view: 'menu' }, '', '#menu');
            }
        }

        // 2. Handle Browser Navigation (Back/Forward)
        const handlePopState = (event) => {
            const stateView = event.state?.view;
            if (stateView) {
                game.setView(stateView);
            } else {
                // Fallback for manual URL edits or legacy links
                const currentHash = window.location.hash.slice(1);
                const viewPart = currentHash.split('#')[0];
                game.setView(viewPart || 'menu');
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [game.setView]);

    // 3. Sync App State Changes to URL (Push State)
    React.useEffect(() => {
        const currentHash = window.location.hash.slice(1);
        const currentViewInHash = currentHash.split('#')[0];

        // Only push if the view has actually changed and the current URL doesn't already reflect it
        if (game.view !== currentViewInHash) {
            window.history.pushState({ view: game.view }, '', `#${game.view}`);
        }
    }, [game.view]);

    // Fetch social counts and check achievements when in menu
    React.useEffect(() => {
        if (game.view === 'menu' && user) {
            social.fetchUnreadCounts().then(res => {
                if (res && res.friendCount > 0) {
                    game.checkProgress('social_friend_added');
                }
            });
        }
    }, [game.view, user]);

    // Check for retroactive weekly challenge achievement
    React.useEffect(() => {
        if (weeklyChallenge?.completed) {
            game.checkProgress('weekly_challenge_complete');
        }
    }, [weeklyChallenge?.completed, game.checkProgress]);

    // Sync data with cloud
    const { isSyncing, lastSynced, saveToCloud, resetUserData } = useDataSync(userSettings, setUserSettings, game.progress, game.setProgress);

    // Keep ref updated for use in handleGameUpdate (defined before useDataSync)
    useEffect(() => {
        saveToCloudRef.current = saveToCloud;
    }, [saveToCloud]);

    // Track mode completions for timeline unlocks
    useModeTracking(game.view, game.gameMode, game.sessionStart, setUserSettings);

    // Skill Assessment state
    const [showSkillAssessment, setShowSkillAssessment] = useState(false);

    // Check if user needs skill assessment (after auth is loaded)
    React.useEffect(() => {
        if (!loading && !userSettings.skill_assessment_completed) {
            // Haven't completed assessment - show modal
            setShowSkillAssessment(true);
        }
    }, [loading, userSettings.skill_assessment_completed]);

    // Handle skill level selection from assessment
    const handleSkillLevelSet = async (level, callSign, username) => {
        const updated = {
            ...userSettings,
            skill_level: level,
            skill_level_updated_at: new Date().toISOString(),
            skill_assessment_completed: true,
            callSign: callSign || userSettings.callSign || '',
            username: username || callSign || userSettings.username || '', // Use username if provided, fallback to callSign
        };
        setUserSettings(updated);
        localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
        setShowSkillAssessment(false);

        // Save to cloud if user is logged in
        if (user) {
            await saveToCloud(updated);
        }

        // Auto-select game mode based on level
        if (level === 1) game.setGameMode('guided');
        else if (level === 2) game.setGameMode('callsigns');
        else if (level === 3) game.setGameMode('callsigns_full');
        else if (level === 4) game.setGameMode('icr_echo');
    };

    // Enable Wake Lock when in game view
    useWakeLock(game?.view === 'game');

    // Check for broken streak on load
    React.useEffect(() => {
        // Skip check if still loading or if we are logged in but don't have our real settings yet
        if (loading || (user && !userSettings.userId)) return;

        const protection = streakShield.shouldProtectStreak(userSettings.lastPlayedDate);

        if (protection.isBroken) {
            // Streak truly broken! Reset to 0.
            const updated = { ...userSettings, streak: 0 };
            setUserSettings(updated);
            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));

            // Sync to cloud if user is logged in
            if (user && saveToCloud) {
                saveToCloud(updated);
            }
        } else if (protection.shouldProtect) {
            console.log(`Streak protectable by ${protection.requiredFreezes} freeze(s). Waiting for activity to consume.`);
        }
    }, []);

    // Track max WPM achieved and WPM history
    React.useEffect(() => {
        const today = getLocalCalendarDay();
        let updated = null;

        // Calculate session stats for verification
        const durationSeconds = (Date.now() - game.sessionStart) / 1000;
        const accuracy = game.sessionStats.total > 0
            ? (game.sessionStats.correct / game.sessionStats.total) * 100
            : 100;

        const isVerified = durationSeconds >= 60 && accuracy >= 90;

        if (isVerified) {
            // Update Max WPM
            if (game.wpm > (userSettings.maxWpm || 0)) {
                updated = { ...(updated || userSettings), maxWpm: game.wpm };
            }

            // Update WPM History (Max per day)
            const history = userSettings.wpmHistory || [];
            const lastEntry = history[history.length - 1];

            if (!lastEntry || lastEntry.date !== today) {
                // New day, add entry
                updated = {
                    ...(updated || userSettings),
                    wpmHistory: [...history, { date: today, wpm: game.wpm }]
                };
            } else if (game.wpm > lastEntry.wpm) {
                // Same day, new max
                const newHistory = [...history];
                newHistory[newHistory.length - 1] = { date: today, wpm: game.wpm };
                updated = {
                    ...(updated || userSettings),
                    wpmHistory: newHistory
                };
            }
        }

        if (updated) {
            setUserSettings(updated);
            localStorage.setItem('morse_settings_v1', JSON.stringify(updated));
        }
    }, [game.wpm, game.score, game.sessionStart]);

    // Check streak achievements
    React.useEffect(() => {
        if (game?.checkProgress && userSettings.streak) {
            game.checkProgress('streak_update', { streak: userSettings.streak });
        }
    }, [userSettings.streak, game.checkProgress]);


    const [dailyTrivia, setDailyTrivia] = useState(null);

    React.useEffect(() => {
        import('./data/trivia').then(m => setDailyTrivia(m.getTriviaForDate()));
    }, []);

    const [knowledgeTab, setKnowledgeTab] = useState('timeline');

    // Factory Reset Logic
    const handleFactoryReset = async () => {
        if (!window.confirm('DANGER: This will wipe ALL your stats, progress, and settings. Are you sure?')) return;

        const defaults = {
            defaultWpm: 20,
            farnsworthWpm: 20,
            toneFrequency: 600,
            inputMode: 'paddle',
            keyMap: { dot: 'ControlLeft', dash: 'ControlRight' },
            theme: 'default',
            userId: user?.id
        };

        const resetProgress = {
            level: 1,
            maxLevel: 1,
            history: [],
            totalSessions: 0,
            accurateSessions: 0,
            totalCharsCopied: 0,
            callsignCount: 0,
            paddleCharsSent: 0,
            totalQSOs: 0,
            qsbSessions: 0,
            echoCompleted: 0
        };

        // 1. Update State
        setUserSettings(defaults);
        game.setProgress(resetProgress);

        // 2. Clear Local Storage
        localStorage.setItem('morse_settings_v1', JSON.stringify(defaults));
        localStorage.setItem('morse_progress_v2', JSON.stringify(resetProgress));

        // 3. Force Cloud Reset
        if (user) {
            try {
                await resetUserData();
                alert('Account data has been reset.');
            } catch (err) {
                console.error(err);
                alert('Local data reset, but cloud reset encountered an error.');
            }
        }

        window.location.reload();
    };

    const renderView = () => {
        if (game.view.startsWith('settings')) {
            const initialTab = game.view === 'settings' ? 'general' : game.view.split('/')[1];
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <SettingsView
                        initialTab={initialTab}
                        initialSettings={userSettings}
                        setUserSettings={setUserSettings}
                        setView={game.setView}
                        onResetAccount={handleFactoryReset}
                        currentProgress={game.progress}
                        syncStatus={{ isSyncing, lastSynced, saveToCloud }}
                        onSave={(newSettings) => {
                            setUserSettings(newSettings);
                            game.setCallSignRegion(newSettings.country);
                            game.setWpm(newSettings.defaultWpm || 20);
                            game.setNoiseLevel(newSettings.noiseLevel || 0);
                            game.setQsbLevel(newSettings.qsbLevel || 0);
                            if (newSettings.inputMode) {
                                game.setInputMode(newSettings.inputMode);
                            }
                            localStorage.setItem('morse_settings_v1', JSON.stringify(newSettings));
                            game.setView('menu');
                        }}
                        onCancel={() => game.setView('menu')}
                        onLegalClick={(tab) => game.setView(tab === 'tos' ? 'legal_tos' : 'legal')}
                    />
                </Suspense>
            );
        }

        if (game.view === 'menu') {
            // Show landing page for new, unauthenticated users
            if (!user && !landingDismissed) {
                return (
                    <Suspense fallback={<LoadingSkeleton type="page" />}>
                        <LandingView
                            onStartGuest={handleStartGuest}
                            onShowAuth={() => setShowLoginPrompt(true)}
                        />
                        {showLoginPrompt && (
                            <Suspense fallback={null}>
                                <AuthView onCancel={() => setShowLoginPrompt(false)} />
                            </Suspense>
                        )}
                    </Suspense>
                );
            }

            return (
                <>
                    {showLoginPrompt && (
                        <Suspense fallback={null}>
                            <AuthView onCancel={() => setShowLoginPrompt(false)} />
                        </Suspense>
                    )}
                    <MenuView
                        user={user}
                        userSettings={userSettings}
                        audio={audio}
                        dailyChallenge={dailyChallenge}
                        dailyDefinition={dailyDefinition}
                        weeklyChallenge={weeklyChallenge}
                        weeklyDefinition={weeklyDefinition}
                        dailyTrivia={dailyTrivia}
                        setView={game.setView}
                        wpm={game.wpm}
                        setWpm={game.setWpm}
                        farnsworthWpm={game.farnsworthWpm}
                        setFarnsworthWpm={game.setFarnsworthWpm}
                        noiseLevel={game.noiseLevel}

                        setNoiseLevel={game.setNoiseLevel}
                        qsbLevel={game.qsbLevel}
                        setQsbLevel={game.setQsbLevel}
                        inputMode={game.inputMode}
                        setInputMode={game.setInputMode}
                        gameMode={game.gameMode}
                        setGameMode={game.setGameMode}
                        callSignRegion={game.callSignRegion}
                        setCallSignRegion={game.setCallSignRegion}
                        activityType={game.activityType}
                        setActivityType={game.setActivityType}
                        activityRole={game.activityRole}
                        setActivityRole={game.setActivityRole}
                        contestType={game.contestType}
                        setContestType={game.setContestType}
                        qsoRole={game.qsoRole}
                        setQsoRole={game.setQsoRole}
                        startGame={game.startGame}
                        setKnowledgeTab={setKnowledgeTab}
                        progress={game.progress}
                        setLevel={game.setLevel}
                        pileupCount={game.pileupCount}
                        setPileupCount={game.setPileupCount}
                        unreadSocialCount={social.unreadCount}
                        customChars={game.customChars}
                        setCustomChars={game.setCustomChars}
                        comprehensionMode={game.comprehensionMode}
                        setComprehensionMode={game.setComprehensionMode}
                        setCurrentLiteratureId={game.setCurrentLiteratureId}
                        setCurrentSegmentIndex={game.setCurrentSegmentIndex}
                        quizInputMode={game.quizInputMode}
                        setQuizInputMode={game.setQuizInputMode}
                    />

                    {showSkillAssessment && !showLoginPrompt && (
                        <Suspense fallback={null}>
                            <SkillAssessmentModal
                                onComplete={handleSkillLevelSet}
                                initialCallSign={userSettings.callSign}
                                initialUsername={userSettings.username}
                            />
                        </Suspense>
                    )}
                </>
            );
        }

        if (game.view === 'learn') {
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <DictionaryView wpm={game.wpm} setView={game.setView} audio={audio} />
                </Suspense>
            );
        }

        if (game.view === 'literature_library') {
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <LiteratureLibraryView
                        setView={game.setView}
                        startGame={game.startGame}
                        setCurrentLiteratureId={game.setCurrentLiteratureId}
                        setCurrentSegmentIndex={game.setCurrentSegmentIndex}
                        userSettings={userSettings}
                    />
                </Suspense>
            );
        }

        if (game.view.startsWith('knowledge')) {
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <KnowledgeBaseView
                        setView={game.setView}
                        audio={audio}
                        wpm={game.wpm}
                        farnsworth={game.farnsworthWpm}
                        unlockedTriviaIds={userSettings.unlockedTrivia}
                        onUnlock={unlockTrivia}
                        initialTab={knowledgeTab}
                        user={user}
                        userStats={{
                            totalSessions: game.progress?.totalSessions || 0,
                            accurateSessions: game.progress?.accurateSessions || 0,
                            totalCharactersCopied: game.progress?.totalCharsCopied || 0,
                            paddleCharsSent: game.progress?.paddleCharsSent || 0,
                            bestWPM: userSettings.maxWpm || 0,
                            totalCallsignsCopied: game.progress?.callsignCount || 0,
                            totalQSOs: game.progress?.totalQSOs || 0,
                            skillLevel: userSettings.skill_level || 0,
                            achievementCount: userSettings.achievements?.length || 0,
                            currentStreak: userSettings.streak || 0,
                            qsbSessions: game.progress?.qsbSessions || 0,
                            echoCompleted: game.progress?.echoCompleted || 0,
                            contestCompleted: userSettings.contestCompleted || 0,
                            pileupCompleted: userSettings.pileupCompleted || 0,
                            headCopyWords: userSettings.headCopyWords || 0,
                        }}
                    />
                </Suspense>
            );
        }

        if (game.view === 'gameover') {
            return <GameOverView
                score={game.score}
                startGame={game.startGame}
                setView={game.setView}
                gameState={{
                    maxWpm: game.wpm || 0,
                    gameMode: game.gameMode,
                    inputMode: game.inputMode,
                    accuracy: 0
                }}
                userSettings={userSettings}
            />;
        }

        if (game.view === 'stats') {
            return (
                <Suspense fallback={<LoadingSkeleton type="page" />}>
                    <StatsView setView={game.setView} setGameMode={game.setGameMode} setCustomChars={game.setCustomChars} startGame={game.startGame} progress={game.progress} score={game.score} lifetimeScore={userSettings.lifetimeScore || 0} maxWpm={userSettings.maxWpm || 0} yyzKeyed={userSettings.yyzKeyed || false} cwoKeyed={userSettings.cwoKeyed || false} micUsed={userSettings.micUsed || false} streak={userSettings.streak || 0} unlockedTriviaCount={userSettings.unlockedTrivia.length} wpmHistory={userSettings.wpmHistory || []} userSettings={userSettings} />
                </Suspense>
            );
        }

        if (game.view === 'leaderboard') {
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <LeaderboardView setView={game.setView} userSettings={userSettings} setSelectedUserId={setSelectedUserId} />
                </Suspense>
            );
        }

        if (game.view === 'user_profile') {
            return (
                <Suspense fallback={<LoadingSkeleton type="page" />}>
                    <UserProfileView userId={selectedUserId} onBack={() => game.setView('leaderboard')} />
                </Suspense>
            );
        }

        if (game.view === 'adventure') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <AdventureView game={game} audio={audio} setView={game.setView} achievements={{ checkProgress: game.checkProgress }} />
                </Suspense>
            );
        }

        if (game.view === 'waterfall') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <WaterfallView setView={game.setView} />
                </Suspense>
            );
        }

        if (game.view === 'gamecenter') {
            return (
                <Suspense fallback={<LoadingSkeleton type="page" />}>
                    <GameCenterView setView={game.setView} startGame={game.startGame} inputMode={game.inputMode} />
                </Suspense>
            );
        }

        if (game.view === 'social') {
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <SocialView setView={game.setView} audio={audio} userSettings={userSettings} />
                </Suspense>
            );
        }

        if (game.view === 'lab') {
            return (
                <Suspense fallback={<LoadingSkeleton type="page" />}>
                    <LabView setView={game.setView} inputMode={game.inputMode} />
                </Suspense>
            );
        }

        // Secret Experimental Route - Not indexed, no public links
        if (game.view === 'midnight_express') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <MidnightExpressView
                        setView={game.setView}
                        audio={audio}
                        userSettings={userSettings}
                        checkProgress={game.checkProgress}
                        game={game}
                    />
                </Suspense>
            );
        }


        if (game.view === 'proqso') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <ProQSOView game={game} setView={game.setView} />
                </Suspense>
            );
        }

        if (game.view === 'signal_lamp') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <SignalLampView
                        setView={game.setView}
                        userSettings={userSettings}
                        audio={audio}
                        checkProgress={game.checkProgress}
                        saveActivity={game.saveActivity}
                    />
                </Suspense>
            );
        }

        if (game.view === 'telegraph_hangman') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <TelegraphHangmanView
                        setView={game.setView}
                        userSettings={userSettings}
                        audio={audio}
                        checkProgress={game.checkProgress}
                        game={game}
                    />
                </Suspense>
            );
        }

        if (game.view === 'signal_word') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <SignalWordView
                        setView={game.setView}
                        audio={audio}
                        game={game}
                    />
                </Suspense>
            );
        }

        if (game.view === 'constellation_hunter') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <ConstellationHunterView
                        setView={game.setView}
                        audio={audio}
                        userSettings={userSettings}
                        checkProgress={game.checkProgress}
                        game={game}
                    />
                </Suspense>
            );
        }

        if (game.view === 'admin') {
            return (
                <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                    <AdminDashboard onBack={() => game.setView('menu')} />
                </Suspense>
            );
        }

        if (game.view.startsWith('legal')) {
            const initialTab = game.view === 'legal_tos' ? 'tos' : 'privacy';
            return (
                <Suspense fallback={<LoadingSkeleton type="text" />}>
                    <LegalView initialTab={initialTab} onBack={() => game.setView('settings/account')} />
                </Suspense>
            );
        }

        return (
            <Suspense fallback={<LoadingSkeleton type="dashboard" />}>
                <GameView
                    setView={game.setView}
                    game={game}
                    setKnowledgeTab={setKnowledgeTab}
                    userSettings={userSettings}
                    audio={audio}
                />
            </Suspense>
        );
    };

    const isLandingActive = game.view === 'menu' && !user && !landingDismissed;

    const showFooter = !isLandingActive &&
        game.view !== 'game' &&
        game.view !== 'adventure' &&
        game.view !== 'waterfall' &&
        game.view !== 'midnight_express' &&
        game.view !== 'signal_lamp' &&
        game.view !== 'signal_word' &&
        game.view !== 'admin' &&
        game.gameMode !== 'signal_lamp';

    return (
        <div className="flex flex-col min-h-screen bg-base">
            <SEO />
            <main className="flex-1">
                {renderView()}
            </main>
            {showFooter && <AppFooter userSettings={userSettings} setView={game.setView} setKnowledgeTab={setKnowledgeTab} />}
        </div>
    );
};

export default MorseApp;
